import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    FormEntity,
    IUseQueryPaged,
    MutationResult,
    PagedResponse,
    QueryHookParams,
    QueryType,
} from "../models";
import { array } from "zod";

export interface IUseFormQuery extends Pick<IUseQueryPaged<FormEntity>, "pagedListQuery" | "listQuery"> {
    sendFileMutation: MutationResult<boolean, unknown, FormData, boolean>;
}
interface IUseFormQueryParams extends QueryHookParams {
    formSlug?: string;
    toEmail?: string;
}

export const useFormQuery = (params?: IUseFormQueryParams): IUseFormQuery => {
    const formId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Forms);
    const url = Endpoints.Forms;
    const queryType = getQueryType(formId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<FormEntity>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.forms.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<FormEntity>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.forms.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const sendFileMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, boolean>({
            url: `${url}/send-email`,
            data,
            config: {
                params: {
                    formSlug: params?.formSlug,
                    toEmail: params?.toEmail
                }
            }
        }),
        queryKey: queryKeys.forms.detail(formId),
        keysToInvalidate: [],
        successToast: "Email sent successfully",
    });

    return useMemo<IUseFormQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            sendFileMutation
        }),
        [pagedListQuery, listQuery, sendFileMutation]
    );
};
